import * as React from 'react';

import { ApplicationState } from 'clients/app/src/rootReducer';
import { useDispatch, useSelector } from 'react-redux';

import { SmallButton } from '~shared/components/buttons';
import { IconPlus } from '~shared/components/icons/standard';
import { QuizAnswer } from '~teacher/models/quizzes';

import { addAnswerToQuestion } from '../../slices/quiz/quizSlice';
import { QuestionMatchingAnswerPairDisplay } from './QuestionMatchingAnswerPairDisplay';
import { QuestionMatchingAnswerPairEdit } from './QuestionMatchingAnswerPairEdit';

export function QuestionMatchingAnswerPairList({
  answerIds,
  isEditing,
}: {
  answerIds: (string | number)[];
  isEditing: boolean;
}) {
  const dispatch = useDispatch();

  const {
    entities,
    answerPromptIdByOrder,
    answerMatchIdByOrder,
  }: {
    entities: Map<string, QuizAnswer>;
    answerPromptIdByOrder: Map<number, string | number>;
    answerMatchIdByOrder: Map<number, string | number>;
  } = useSelector((state: ApplicationState) =>
    answerIds
      .map(
        (answerId) =>
          state.teachers.quizData.answers[answerId] ??
          state.teachers.quizData.editAnswers[answerId]
      )
      .reduce(
        ({ entities, answerPromptIdByOrder, answerMatchIdByOrder }, answer) => {
          return {
            answerPromptIdByOrder:
              answer.order > 1000
                ? answerPromptIdByOrder
                : answerPromptIdByOrder.set(answer.order, answer.id),
            answerMatchIdByOrder:
              answer.order < 1000
                ? answerMatchIdByOrder
                : answerMatchIdByOrder.set(answer.order, answer.id),
            entities: entities.set(answer.id as string, answer),
          };
        },
        {
          entities: new Map<string, QuizAnswer>(),
          answerPromptIdByOrder: new Map<number, string | number>(),
          answerMatchIdByOrder: new Map<number, string | number>(),
        }
      )
  );

  const answerPairs = [...answerPromptIdByOrder.entries()].map(
    ([order, id]) => {
      const answerPrompt = entities.get(id as string);
      const matchOrder = order + 1000;
      const answerMatchId = answerMatchIdByOrder.get(matchOrder);
      const answerMatch = entities.get(answerMatchId as string);
      return { answerPrompt, answerMatch };
    }
  );

  const addAnswerPair = React.useCallback(() => {
    const newOrder = answerPairs.length + 1;
    dispatch(addAnswerToQuestion({ order: newOrder }));
    dispatch(addAnswerToQuestion({ order: newOrder + 1000 }));
  }, [dispatch, answerPairs.length]);

  return (
    <>
      {answerPairs.map(({ answerPrompt, answerMatch }, index) => {
        return isEditing ? (
          <QuestionMatchingAnswerPairEdit
            isDisabled={false}
            key={`${answerPrompt?.id ?? index}-${answerMatch?.id ?? index}`}
            answerPrompt={answerPrompt}
            answerMatch={answerMatch}
          />
        ) : (
          <QuestionMatchingAnswerPairDisplay
            key={`${answerPrompt?.id ?? index}-${answerMatch?.id ?? index}`}
            answerPrompt={answerPrompt}
            answerMatch={answerMatch}
          />
        );
      })}
      {isEditing ? (
        <div className="mt-4">
          <SmallButton
            isOnWhite={false}
            onClick={addAnswerPair}
            isDisabled={false}
          >
            <span className="inline-flex mr-1 -ml-2 text-inherit">
              <IconPlus />
            </span>
            Add Pair
          </SmallButton>
        </div>
      ) : null}
    </>
  );
}
